import { PaymentSheetEventsEnum, Stripe } from '@capacitor-community/stripe'
import { Capacitor } from '@capacitor/core'
import type { Pinia } from 'pinia'
import useStripePaymentSheetStore from '~/store/stripePaymentSheet'

type StripeClient = Pick<typeof Stripe, 'addListener' | 'initialize'>
type CapacitorClient = Pick<typeof Capacitor, 'isNativePlatform' | 'isPluginAvailable'>

interface StripeBootstrapDependencies {
  stripe?: StripeClient
  capacitor?: CapacitorClient
  publishableKey?: string
}

export function getStripeErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && 'error' in error && typeof error.error === 'string') return error.error
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string')
    return error.message
  return 'Payment failed'
}

export async function initialiseStripePaymentSheet(
  pinia: Pinia,
  dependencies: StripeBootstrapDependencies = {}
): Promise<void> {
  const stripe = dependencies.stripe ?? Stripe
  const capacitor = dependencies.capacitor ?? Capacitor

  if (!capacitor.isNativePlatform() || !capacitor.isPluginAvailable('Stripe')) return

  const stripePaymentSheetStore = useStripePaymentSheetStore(pinia)

  try {
    await Promise.all([
      stripe.addListener(PaymentSheetEventsEnum.Completed, () => {
        stripePaymentSheetStore.publishResult({ status: 'completed' })
      }),
      stripe.addListener(PaymentSheetEventsEnum.Canceled, () => {
        stripePaymentSheetStore.publishResult({ status: 'canceled' })
      }),
      stripe.addListener(PaymentSheetEventsEnum.Failed, (error) => {
        stripePaymentSheetStore.publishResult({ status: 'failed', error: getStripeErrorMessage(error) })
      })
    ])
  } catch (error) {
    console.error('Failed to register Stripe PaymentSheet listeners:', error)
  }

  const publishableKey = dependencies.publishableKey ?? import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  if (!publishableKey) {
    console.warn('Stripe publishable key is not set in environment variables.')
    return
  }

  try {
    await stripe.initialize({ publishableKey })
  } catch (error) {
    console.error('Failed to initialize Stripe:', error)
  }
}
